import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { AlertController, IonReorderGroup, ModalController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { ToastService } from '../../../_core/services/toast.service';
import { TeamService } from '../../../_core/services/team.service';
import { Team } from '../../../_shared/models/team.model';
import { Template } from '../../../_shared/models/template.model';
import { ItemReorderEventDetail } from '@ionic/core';
import { SelectTeamPlayerComponent } from '../../../_shared/components/select-team-player/select-team-player.component';
import { HelperService } from '../../../_core/services/helper.service';
import { TeamDetailsService } from '../team-details.service';
import { Router } from '@angular/router';

@Component({
  standalone: false,
    selector: 'app-team-lineup',
    templateUrl: './team-lineup.component.html',
    styleUrls: ['./team-lineup.component.scss'],
})

export class TeamLineupComponent implements OnInit {
    @ViewChild(IonReorderGroup) reorderGroup: IonReorderGroup;

    @Input() team: Team;
    template: Template;

    private readonly destroy$: Subject<void> = new Subject();
    addingLineup: boolean;
    isLoading: boolean;
    removingLineup: boolean;
    removingPosition: boolean;
    selectedPosition: { collectionIndex: number; positionAssignmentIndex: number; };
    updateLineupTimeout: any;
    updatingLineup: boolean;
    showUpdateLineupCheckmark: boolean;

    constructor(
        private teamService: TeamService,
        private teamDetailsService: TeamDetailsService,
        private toast: ToastService,
        private modalController: ModalController,
        private alertController: AlertController,
        public helper: HelperService,
        private router: Router
    ) { }

    ngOnInit() {
        this.team = this.teamDetailsService.teamInfo;
        this.getTeamLineups();
    }

    getTeamLineups() {
        this.isLoading = true;

        this.teamService
            .getLineup(this.team._id)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: res => {
                    this.template = res.data;
                },
                error: err => {
                    this.router.navigate(['/my-teams', this.team._id, 'lineup']);
                }
            });
    }

    doReorder(ev: CustomEvent<ItemReorderEventDetail>, index) {
        const reorderArray = this.template.lineups[0].lineupCollectionDefinition[index].positionAssignments;
        const itemMove = reorderArray.splice(ev.detail.from, 1)[0];
        reorderArray.splice(ev.detail.to, 0, itemMove);
        reorderArray.forEach((element, i) => {
            element.collectionPosition = i + 1;
        });

        ev.detail.complete();
        this.updateLineup(index);
    }

    toggleReorderGroup() {
        this.reorderGroup.disabled = !this.reorderGroup.disabled;
    }

    addTeamLineup(template) {
        if (template.type && template.subtype) {
            this.addingLineup = true;

            this.teamService
                .addLineup(this.team._id, template)
                .pipe(
                    takeUntil(this.destroy$),
                    finalize(() => this.addingLineup = false)
                )
                .subscribe({
                    next: res => {
                        this.team = res.data;
                        this.getTeamLineups();
                        this.toast.presentToast(res.message);
                    }
                });
        }
    }

    async addParticipant(collectionDefinition, collectionIndex) {
        const modal = await this.modalController.create({
            component: SelectTeamPlayerComponent,
            componentProps: {
                teamId: this.team._id,
                participantType: collectionDefinition.matchUpType === 'SINGLES' ? 'INDIVIDUAL' : 'PAIR'
            },
            presentingElement: await this.modalController.getTop()
        });

        modal.present();

        const { data } = await modal.onDidDismiss();
        if (data) {
            const playerIds = data.map(player => player._id);

            const collectionPosition = this.template.lineups[0].lineupCollectionDefinition[collectionIndex]?.positionAssignments.length + 1;
            this.teamService
                .addParticipantToLineup(this.team._id, this.template._id, collectionDefinition.collectionId, playerIds, collectionIndex, collectionPosition)
                .pipe(
                    takeUntil(this.destroy$),
                    finalize(() => this.addingLineup = false)
                )
                .subscribe({
                    next: res => {
                        this.template = res.data;
                    }
                });
        }
    }

    async updateLineup(collectionIndex) {
        this.showUpdateLineupCheckmark = false;
        if (this.updateLineupTimeout) {
            clearTimeout(this.updateLineupTimeout)
        }

        this.updateLineupTimeout = setTimeout(() => {
            this.updatingLineup = true;

            const positionAssignments = this.template.lineups[0].lineupCollectionDefinition[collectionIndex]?.positionAssignments;
            this.teamService
                .updateLineup(this.team._id, this.template._id, collectionIndex, positionAssignments)
                .pipe(
                    takeUntil(this.destroy$),
                    finalize(() => {
                        this.updatingLineup = false;
                        this.handleUpdateLineupCheckmark();
                    })
                )
                .subscribe({
                    next: res => {}
                });
        }, 2000);
    }

    handleUpdateLineupCheckmark() {
        this.showUpdateLineupCheckmark = true;
        setTimeout(() => {
            this.showUpdateLineupCheckmark = false;
        }, 1000);
    }

    async removeLineup() {
        const alert = await this.alertController.create({
            header: 'Remove lineup',
            message: 'Are you sure you want to remove the lineup. This action cannot be undone. Do you want to continue?',
            buttons: [
                {
                    text: 'Yes, remove it',
                    handler: () => {
                        this.handleRemovingLineup();
                    }
                },
                {
                    text: 'No, keep it',
                    role: 'cancel'
                }
            ]
        });

        await alert.present();
    }

    handleRemovingLineup() {
        this.removingLineup = true;

        this.teamService
            .removeLineup(this.team._id, this.template._id, this.template.lineups[0]._id)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.removingLineup = false)
            )
            .subscribe({
                next: res => {
                    this.template.lineups.splice(0, 1);
                    this.toast.presentToast(res.message);
                }
            });
    }

    removePosition(collectionIndex: number, collectionPosition: number, positionAssignmentIndex: number) {
        this.selectedPosition = {
            collectionIndex,
            positionAssignmentIndex
        }

        this.removingPosition = true;
        this.teamService
            .removeParticipantFromLineup(this.team._id, this.template._id, collectionIndex, collectionPosition)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.removingPosition = false)
            )
            .subscribe({
                next: res => {
                    this.template.lineups[0].lineupCollectionDefinition[collectionIndex].positionAssignments.splice(positionAssignmentIndex, 1);
                    this.toast.presentToast(res.message);
                }
            });
    }

    getSex(sex) {
        if (sex) {
            if (sex === 'M') return 'Male';
            else return 'Female';
        }
    }

}
